import type { ScriptModel } from "@cuekards/core/script-model";

/** What the setup pane knows about the document: nothing yet, a fetch in flight,
 * a load that worked, or one that did not. */
export type SetupStatus =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "loaded"; docId: string; model: ScriptModel }
  | { kind: "error"; reason: string; message: string };

export interface SetupPaneProps {
  url: string;
  onUrlChange: (url: string) => void;
  onLoad: () => void;
  onRefresh: () => void;
  status: SetupStatus;
  refreshing: boolean;
  /** True while a take is in progress. Loading a different document mid-take
   * would move the prompter under a reader, and refresh is refused outright
   * (FR-17). */
  takeInProgress: boolean;
}

/**
 * Where a script comes from: the URL, Load, Refresh, and what happened
 * (one-window ticket 05).
 *
 * **The v1 `<pre className="script">` preview is deleted rather than moved
 * here.** It existed because the control window could not see the prompter, so a
 * wall of raw text was the only proof a load had worked. The prompter is now what
 * the window shows the instant a script is loaded, at reading size, with the notes
 * already parsed — a second copy of the same text in a smaller font would be the
 * worse of the two. What replaces it is one line saying what arrived, which is
 * all the original was being read for.
 *
 * **Rendered in two places from one set of props** (one-window-polish ticket 05):
 * it is what an empty window shows, and it is the settings modal's Script tab
 * once something is loaded. So the loaded line no longer tells anyone to "open
 * the prompter" — there is no longer anything to open, and on the only surface
 * that line still appears on the prompter is already behind it.
 */
export function SetupPane({
  url,
  onUrlChange,
  onLoad,
  onRefresh,
  status,
  refreshing,
  takeInProgress,
}: SetupPaneProps): JSX.Element {
  // Refresh needs a loaded doc, and is refused mid-take (FR-17) and while a
  // fetch is already in flight.
  const canRefresh = status.kind === "loaded" && !takeInProgress && !refreshing;

  return (
    <section className="setup-pane">
      <form
        className="loader"
        onSubmit={(e) => {
          e.preventDefault();
          onLoad();
        }}
      >
        <input
          type="text"
          value={url}
          placeholder="Paste a Google Doc share link…"
          onChange={(e) => onUrlChange(e.target.value)}
          aria-label="Google Doc URL"
        />
        <button type="submit" disabled={status.kind === "loading" || takeInProgress}>
          {status.kind === "loading" ? "Loading…" : "Load"}
        </button>
        <button
          type="button"
          className="refresh"
          onClick={onRefresh}
          disabled={!canRefresh}
          title={
            takeInProgress
              ? "Refresh is unavailable while recording"
              : "Re-fetch the doc and keep your place"
          }
        >
          {refreshing ? "Refreshing…" : "Refresh"}
        </button>
      </form>

      {status.kind === "error" && (
        <p className="error" role="alert" data-reason={status.reason}>
          {status.message}
        </p>
      )}

      {status.kind === "loaded" && (
        <p className="setup-loaded" role="status">
          {loadedSummary(status.model)}.
        </p>
      )}
    </section>
  );
}

/**
 * One line about what loaded, counted rather than shown.
 *
 * The word count is the alignment index's length, which is the number that
 * actually matters: it is what the follower will be walking, so "0 words" is the
 * useful answer for a document that fetched fine and has nothing readable in it.
 */
function loadedSummary(model: ScriptModel): string {
  const lines = model.lines.length;
  return `Loaded ${count(model.wordCount, "word")} across ${count(lines, "line")}`;
}

function count(n: number, noun: string): string {
  return `${n.toLocaleString()} ${noun}${n === 1 ? "" : "s"}`;
}
